import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const Carousel = ({ items = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // 自动轮播
  const autoPlay = useCallback(() => {
    if (isAutoPlaying && items.length > 0) {
      setCurrentIndex((prevIndex) => 
        prevIndex === items.length - 1 ? 0 : prevIndex + 1
      );
    }
  }, [isAutoPlaying, items.length]);

  // 设置自动轮播定时器
  useEffect(() => {
    const timer = setInterval(autoPlay, 5000);
    return () => clearInterval(timer);
  }, [autoPlay]);

  // 切换到指定幻灯片
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // 5秒后恢复自动播放
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // 上一张
  const prevSlide = () => {
    const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  // 下一张
  const nextSlide = () => {
    const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };

  // 如果没有items数据，不渲染组件
  if (!items || items.length === 0) {
    return (
      <div className="relative h-[300px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-lg bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">暂无轮播内容</p>
      </div>
    );
  }

  return (
    <div className="relative h-[300px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-lg">
      {/* 幻灯片 */}
      <div 
        className="h-full transition-transform duration-500 ease-out flex"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {items.map((slide, index) => (
          <div 
            key={index} 
            className="relative w-full h-full flex-shrink-0"
          >
            <img
              src={slide.imageUrl}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6 text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">{slide.title}</h2>
              <p className="mb-4 text-sm md:text-base">{slide.description}</p>
              <Link 
                to={slide.link || `/product/${slide.productId}`} 
                className="bg-white text-gray-800 px-4 py-2 rounded-full inline-block w-max hover:bg-opacity-90 transition-colors"
              >
                {slide.buttonText}
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* 导航按钮 */}
      <button
        onClick={prevSlide}
        className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/30 hover:bg-white/50 rounded-full p-2 transition-colors"
        aria-label="上一张"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/30 hover:bg-white/50 rounded-full p-2 transition-colors"
        aria-label="下一张"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* 指示器 */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentIndex ? 'bg-white' : 'bg-white/50'
            }`}
            aria-label={`切换到第${index + 1}张幻灯片`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;